import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Box, Grid } from "@mui/system";
import {
  Autocomplete,
  IconButton,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import BudgetControlGraphView from "./BudgetControlChartView";
import BudgetControlTableView from "./BudgetControlTableView";
import { useDispatch, useSelector } from "react-redux";
import BudgetTableSkeleton from "./BudgetTableSkeleton";
import BudgetGraphSkeleton from "./BudgetGraphSkeleton";
import { useBizDevelopmentDashboard } from "../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import { useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FloatingActionItems from "../../../../../pages/FloatingItems/FloatingActionItems";
import { useMemo } from "react";
import { setBrandNameForBudget } from "../../../../../features/bizDevelopmentDashboardSlice";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const BudgetControlTabs = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialView = searchParams.get("viewForBudget") === "table" ? 1 : 0;
  const [value, setValue] = React.useState(initialView); // sub tab state: 0 for graph view, 1 for table view

  const { getSitesFromParams, fetchBudgetData } = useBizDevelopmentDashboard();

  const isLoading = useSelector(
    (state: any) => state.bizDevelopmentDashboard.isLoading,
  );

  const isFetchedBudgetData = useSelector(
    (state: any) => state.bizDevelopmentDashboard.isFetchedBudgetData,
  );

  //is enabled budget overuse toggle
  const budgetOveruse = useSelector(
    (state: any) => state.bizDevelopmentDashboard.isBudgetOverused,
  );

  // Add to BudgetControlTabs, after existing state:
  const budgetDataOfBizDevelopment = useSelector(
    (state: any) => state.bizDevelopmentDashboard.budgetDataOfBizDevelopment,
  );

  //fetch budget metadata for table view columnss
  const budgetMetaDataOfBizDevelopment =
    useSelector(
      (state: any) =>
        state.bizDevelopmentDashboard.budgetMetaDataOfBizDevelopment,
    ) ?? {};

  //fetch brand name
  const brandNameForBudget = useSelector(
    (state: any) => state.bizDevelopmentDashboard.brandNameForBudget,
  );

  // graph view to table view handler
  const handleSubTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newValue === 1) {
        next.set("viewForBudget", "table");
      } else if (newValue === 0) {
        next.set("viewForBudget", "graph");
      }
      return next;
    });
  };

  // Refresh handler to re-fetch budget data
  const handleRefresh = async () => {
    fetchBudgetData();
  };

  const cleanNumber = (value: any): number =>
    parseFloat(String(value ?? "0").replace(/[% ,]/g, "")) || 0;

  const budgetOveruseKeys = ["perc_of_budget_used"];

  // Calculate visible row count based on budget overuse toggle and budget data
  const visibleRowCount = React.useMemo(() => {
    const search = (brandNameForBudget ?? "").trim().toLowerCase();

    // Apply brand name filter first (matches brand_display_name)
    let data = budgetDataOfBizDevelopment ?? [];
    if (search) {
      data = data.filter((row: any) =>
        String(row["brand_display_name"] ?? "")
          .toLowerCase()
          .includes(search),
      );
    }

    if (value === 0) {
      // Graph view — only perc_of_budget_used matters
      if (!budgetOveruse) return data.length;
      return data.filter(
        (row: any) => cleanNumber(row["perc_of_budget_used"]) * 100 >= 100,
      ).length;
    }

    // Table view — any of the three keys >= 100% qualifies
    if (!budgetOveruse) return data.length;
    return data.filter((row: any) =>
      budgetOveruseKeys.some((key) => cleanNumber(row[key]) * 100 >= 100),
    ).length;

    //eslint-disable-next-line
  }, [budgetDataOfBizDevelopment, budgetOveruse, value, brandNameForBudget]);

  //-- Extract unique brand names for Autocomplete filter
  const brandNames = useMemo(() => {
    const set = new Set<string>();
    (budgetDataOfBizDevelopment || []).forEach((r: any) => {
      if (typeof r?.brand_display_name === "string")
        set.add(r.brand_display_name.trim());
    });
    return Array.from(set);
  }, [budgetDataOfBizDevelopment]);

  //Download CSV handler for both graph and table, with same filter logic
  const handleDownloadCSV = () => {
    // filter logic used in both table/graph
    const search = (brandNameForBudget ?? "").trim().toLowerCase();

    // Start from all data, apply brand-name filter first
    let filteredData = budgetDataOfBizDevelopment ?? [];

    if (search) {
      filteredData = filteredData.filter((row: any) =>
        String(row["brand_display_name"] ?? "")
          .toLowerCase()
          .includes(search),
      );
    }

    // Then apply the overuse filter (same logic as before)
    if (budgetOveruse) {
      filteredData = filteredData.filter((row: any) => {
        if (value === 0) {
          // Graph view — only perc_of_budget_used
          return cleanNumber(row["perc_of_budget_used"]) * 100 >= 100;
        }
        // Table view — any of the three keys >= 100% qualifies
        return budgetOveruseKeys.some(
          (key) => cleanNumber(row[key]) * 100 >= 100,
        );
      });
    }
    if (filteredData.length === 0) return;
    //for graph view export csv
    if (value === 0) {
      // Graph view — only brand_display_name and perc_of_budget_used
      const headers = "Brand,% Budget Used";
      const csvRows = filteredData.map((row: any) => {
        const brand = row["brand_display_name"] ?? "";
        const perc =
          row["perc_of_budget_used"] != null
            ? `${Math.round(row["perc_of_budget_used"] * 100)}%`
            : "";
        const escapedBrand = brand.includes(",") ? `"${brand}"` : brand;
        return `${escapedBrand},${perc}`;
      });

      const csvContent = [headers, ...csvRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `budget_control_graph_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      return;
    }

    //for table view export csv with all columns based on metadata
    const toPercent = (value: any): number => {
      if (value === null || value === undefined) return 0;
      return (parseFloat(String(value).replace(/[% ,]/g, "")) || 0) * 100;
    };

    const formatNumber = (value: any): string => {
      const num = parseFloat(String(value).replace(/[, ]/g, ""));
      if (isNaN(num)) return String(value ?? "");
      return Math.abs(num).toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });
    };

    const percentKeys = new Set([
      "perc_of_budget_used",
      "perc_of_qftd_RR_budget_use",
      "perc_of_qftd_budget_used",
    ]);

    const currencyKeys = new Set([
      "cpa_constraint",
      "total_income",
      "cpa_income",
    ]);

    const orderedColumns = Object.entries(budgetMetaDataOfBizDevelopment)
      .filter(([key]) => key !== "total_income_constraint")
      .sort(([, a]: any, [, b]: any) => Number(a.order) - Number(b.order))
      .map(([key, meta]: any) => {
        let title = (meta.title as string).replace(/^Perc Of\s*/i, "% ");
        if (key === "cpa_constraint") title = "CPA Budget";
        if (key === "perc_of_qftd_RR_budget_use") title = "% of RR Budget Used";
        if (key === "perc_of_qftd_budget_used") title = "% QFTD Used";
        return { key, title };
      });

    const headers = orderedColumns.map((col) => col.title).join(",");

    const escapeCell = (value: any): string => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const formatCell = (key: string, value: any): string => {
      if (value === null || value === undefined) return "";

      if (percentKeys.has(key)) {
        return `${toPercent(value).toFixed(1)}%`;
      }

      if (currencyKeys.has(key)) {
        const num = parseFloat(String(value).replace(/[, ]/g, ""));
        if (isNaN(num)) return "";
        return num < 0 ? `-$${formatNumber(value)}` : `$${formatNumber(value)}`;
      }

      if (key === "remaining_qftd") {
        if (value === null || value === undefined || value === "") return "";
        const num = Number(String(value).replace(/[, ]/g, ""));
        return Number.isFinite(num) ? String(Math.trunc(num)) : "";
      }

      if (key === "qftd_budget") {
        if (value === null || value === undefined || value === "") return "";
        const num = Number(String(value).replace(/[, ]/g, ""));
        return Number.isFinite(num)
          ? `$${Math.floor(num).toLocaleString("en-US")}`
          : "";
      }

      return escapeCell(value);
    };

    const csvRows = filteredData.map((row: any) =>
      orderedColumns
        .map((col) => escapeCell(formatCell(col.key, row[col.key])))
        .join(","),
    );

    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `budget_control_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2} alignItems="center" p={2}>
          <Grid size={6}>
            <Box display={"flex"} alignItems={"center"}>
              <Typography fontSize={"18px"} mr={2}>
                Budget Use by Brands
              </Typography>
              {/* Tabs for Graph View and Table View */}
              <Tabs
                value={value}
                onChange={handleSubTabChange}
                aria-label="basic tabs example"
                sx={{
                  background: "#F5F5F5",
                  borderRadius: "8px",
                  padding: "4px",
                  minHeight: "30px",
                  "& .MuiTab-root": {
                    color: "#00000061",
                    padding: "5px 16px",
                    minHeight: "30px",
                    "&.Mui-selected": {
                      color: "#000000DE",
                      background: "#ffffff",
                      borderRadius: "4px",
                    },
                  },

                  /* Active line color */
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#9C27B0",
                    height: "0px", // optional
                    borderRadius: "10px", // optional
                  },
                }}
              >
                <Tab
                  data-tracker="817"
                  icon={<BarChartOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                  label="Graph View"
                  {...a11yProps(0)}
                />
                <Tab
                  data-tracker="818"
                  icon={<TableChartOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                  label="Table View"
                  {...a11yProps(1)}
                />
              </Tabs>
            </Box>
          </Grid>
          <Grid size={6}>
            <Box display={"flex"} justifyContent={"flex-end"}>
              {/* Brand Name Autocomplete for both graph and table */}
              <Autocomplete
                fullWidth
                disabled={
                  budgetDataOfBizDevelopment.length === 0 ||
                  !isFetchedBudgetData
                } //disable when no data + fetching data
                disablePortal
                freeSolo //user can type anything
                forcePopupIcon //show dropdown arrow even in freeSolo mode
                data-tracker="620"
                sx={{
                  width: 350,
                  // ↓ Always show clear button when searchbox has value
                  "& .MuiAutocomplete-clearIndicator": {
                    visibility: brandNameForBudget ? "visible" : "hidden",
                  },
                }}
                options={brandNames}
                value={brandNameForBudget || ""}
                disableClearable={!brandNameForBudget} // Hide clear icon when empty
                onInputChange={(_, newVal) => {
                  //Every keystroke → Redux → shows X button
                  dispatch(setBrandNameForBudget(newVal ?? ""));
                }}
                onChange={(_, newValue: string | null) => {
                  dispatch(setBrandNameForBudget(newValue ?? ""));
                }}
                getOptionLabel={(opt) => opt || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand Name"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ marginLeft: "12px" }} />
                        </InputAdornment>
                      ),
                    }}
                    color="secondary"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault(); // Prevent default form submission
                        const inputValue = (
                          e.target as HTMLInputElement
                        ).value.trim();

                        if (inputValue) {
                          // Dispatch the search term
                          dispatch(setBrandNameForBudget(inputValue));

                          // Close the dropdown by blurring the input
                          (e.target as HTMLInputElement).blur();
                        }
                      }
                    }}
                  />
                )}
                size="small"
              />
              {/* Refresh button for both graph and table */}
              <IconButton
                data-tracker="819"
                aria-label="fingerprint"
                color="secondary"
                disabled={isLoading}
                onClick={handleRefresh}
              >
                <RefreshIcon />
              </IconButton>
              {/* Download CSV for both graph and table */}
              <IconButton
                data-tracker="820"
                aria-label="fingerprint"
                color="secondary"
                sx={{ ml: 2 }}
                onClick={handleDownloadCSV}
                disabled={visibleRowCount === 0 || !isFetchedBudgetData}
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <CustomTabPanel value={value} index={0}>
          {!isFetchedBudgetData ? (
            <BudgetGraphSkeleton />
          ) : (
            <BudgetControlGraphView />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {!isFetchedBudgetData ? (
            <BudgetTableSkeleton />
          ) : (
            <BudgetControlTableView />
          )}
        </CustomTabPanel>
      </Box>
      <div data-tracker="822">
        <FloatingActionItems
          selectedSiteFloatingAction={getSitesFromParams().join(",")}
          selectedDimensionsFloatingAction=""
          trackIdFloatingAction="823"
          disabledButton={budgetDataOfBizDevelopment?.length === 0}
        />
      </div>
    </>
  );
};

export default BudgetControlTabs;
